"use client";

import { ArrowsLeftRightIcon } from "@phosphor-icons/react/dist/ssr";
import { useTranslations } from "next-intl";

import EmptyStateMessage from "@/components/ui/emptyStateMessage";
import { getHomeWorkflowChartColorClass } from "@/features/home/utils/homeTagColors";
import type { WorkflowOutput } from "@/types";

type WorkflowAviCardProps = {
  data: WorkflowOutput | undefined;
};

export function WorkflowAviCard({ data }: WorkflowAviCardProps) {
  const t = useTranslations("home");
  const tGlossary = useTranslations("glossary");
  const isEmpty = !data || data.total === 0;

  return (
    <div className="flex w-full flex-1 flex-col gap-2">
      {isEmpty ? (
        <div className="flex h-35.75 items-center justify-center">
          <EmptyStateMessage
            message={t("empty.workflow")}
            icon={<ArrowsLeftRightIcon size={24} className="color-neutral-950" />}
            compact
          />
        </div>
      ) : (
        <>
          {/* Header */}
          <div className="flex items-center gap-2">
            <div className="flex size-6 shrink-0 flex-row items-center justify-center gap-[5.54px] rounded-lg bg-[#D5F2F0] p-0">
              <ArrowsLeftRightIcon size={14} className="text-[#1A1A1A]" />
            </div>
            <span className="heading-6 translate-y-0.5 text-neutral-950">
              {t("section.workflow")}
            </span>
          </div>

          {/* Card */}
          <div className="flex flex-col rounded-md bg-neutral-50">
            <div className="flex w-full px-4 pt-3 pb-2 xl:pt-4 xl:pb-3">
              {data!.items.map((item) => (
                <div
                  key={item.status}
                  className={`h-2 rounded-3xl ${getHomeWorkflowChartColorClass(item.status)}`}
                  style={{ flex: item.percentage }}
                />
              ))}
            </div>

            <div className="border-t border-neutral-100" />

            <div className="flex items-center justify-between gap-2 px-4 py-2 xl:py-3">
              {data!.items.map((item) => (
                <div key={item.status} className="flex w-fit items-start gap-1">
                  <div className="flex size-4 shrink-0 items-center justify-center">
                    <div
                      className={`size-2 rounded-full ${getHomeWorkflowChartColorClass(item.status)}`}
                    />
                  </div>
                  <div className="flex min-w-0 flex-col gap-1">
                    <span className="font-body truncate text-[10px] leading-4 text-neutral-950 2xl:text-xs">
                      {tGlossary(`workflowStatus.${item.status}`)}
                    </span>
                    <span className="font-body text-[10px] leading-4 text-neutral-600 2xl:text-xs">
                      {item.count} ({Math.round(item.percentage)}%)
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
